import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useAdminAuth, formatApiError } from "@/admin/AdminAuthContext";
import { toast } from "sonner";
import { Loader2, Check, X, Trash2, Plus, LogOut, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtDate(iso, locale = "en") {
  if (!iso) return "—";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString(
      locale,
      { day: "2-digit", month: "short", year: "numeric" }
    );
  } catch {
    return iso;
  }
}

function nightsBetween(a, b) {
  try {
    const ms = new Date(b).getTime() - new Date(a).getTime();
    return Math.max(0, Math.round(ms / 86400000));
  } catch {
    return 0;
  }
}

const STATUS_STYLES = {
  pending: "bg-[#EAE4D9] text-[#5C625E] border-[#5C625E]/30",
  confirmed: "bg-[#8A9A86]/20 text-[#3d4a3a] border-[#8A9A86]/50",
  declined: "bg-[#C05C3D]/10 text-[#a64c30] border-[#C05C3D]/40",
};

export const AdminDashboard = () => {
  const { admin, logout, authHeader } = useAdminAuth();
  const [bookings, setBookings] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [settings, setSettings] = useState(null);
  const [seasonals, setSeasonals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // id -> true
  const [newRange, setNewRange] = useState({ start_date: "", end_date: "", label: "" });
  const [newSeasonal, setNewSeasonal] = useState({
    start_date: "",
    end_date: "",
    nightly_price: "",
    label: "",
  });
  const [settingsDraft, setSettingsDraft] = useState(null);

  const config = useMemo(() => ({ headers: authHeader() }), [authHeader]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r, s, sp] = await Promise.all([
        axios.get(`${API}/admin/bookings`, config),
        axios.get(`${API}/admin/booked-ranges`, config),
        axios.get(`${API}/admin/settings`, config),
        axios.get(`${API}/admin/seasonal-prices`, config),
      ]);
      setBookings(b.data || []);
      setRanges(r.data || []);
      setSettings(s.data || null);
      setSettingsDraft(s.data || null);
      setSeasonals(sp.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateStatus = async (id, status) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await axios.patch(`${API}/admin/bookings/${id}/status`, { status }, config);
      toast.success(
        status === "confirmed"
          ? "Booking confirmed — dates blocked."
          : status === "declined"
          ? "Booking declined."
          : "Status updated."
      );
      await fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const deleteRange = async (id) => {
    if (!window.confirm("Delete this booked range? The dates will become available again.")) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await axios.delete(`${API}/admin/booked-ranges/${id}`, config);
      toast.success("Range deleted.");
      await fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const addRange = async (e) => {
    e.preventDefault();
    if (!newRange.start_date || !newRange.end_date) {
      toast.error("Please pick both dates.");
      return;
    }
    setBusy((b) => ({ ...b, _new: true }));
    try {
      await axios.post(
        `${API}/admin/booked-ranges`,
        {
          start_date: newRange.start_date,
          end_date: newRange.end_date,
          label: newRange.label.trim() || "Booked",
        },
        config
      );
      setNewRange({ start_date: "", end_date: "", label: "" });
      toast.success("Range added.");
      await fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, _new: false }));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy((b) => ({ ...b, _settings: true }));
    try {
      const payload = {
        base_nightly_price: Number(settingsDraft.base_nightly_price),
        currency: settingsDraft.currency,
        weekly_discount_percent: Number(settingsDraft.weekly_discount_percent),
        weekly_discount_min_nights: Number(settingsDraft.weekly_discount_min_nights),
      };
      const { data } = await axios.patch(`${API}/admin/settings`, payload, config);
      setSettings(data);
      setSettingsDraft(data);
      toast.success("Pricing updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, _settings: false }));
    }
  };

  const addSeasonal = async (e) => {
    e.preventDefault();
    if (!newSeasonal.start_date || !newSeasonal.end_date || !newSeasonal.nightly_price) {
      toast.error("Please fill all seasonal price fields.");
      return;
    }
    setBusy((b) => ({ ...b, _newSeasonal: true }));
    try {
      await axios.post(
        `${API}/admin/seasonal-prices`,
        {
          start_date: newSeasonal.start_date,
          end_date: newSeasonal.end_date,
          nightly_price: Number(newSeasonal.nightly_price),
          label: newSeasonal.label.trim() || null,
        },
        config
      );
      setNewSeasonal({ start_date: "", end_date: "", nightly_price: "", label: "" });
      toast.success("Season added.");
      await fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, _newSeasonal: false }));
    }
  };

  const deleteSeasonal = async (id) => {
    if (!window.confirm("Delete this seasonal price?")) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await axios.delete(`${API}/admin/seasonal-prices/${id}`, config);
      toast.success("Season deleted.");
      await fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F5F0] text-[#2C302E]">
      {/* Topbar */}
      <header className="border-b border-[#2C302E]/10 bg-[#F8F5F0]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <a href="/" className="font-serif text-xl tracking-tight">
            Villa Sućuraj <span className="text-[#C05C3D]">· Admin</span>
          </a>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs uppercase tracking-[0.2em] text-[#5C625E]">
              {admin?.email}
            </span>
            <button
              onClick={fetchAll}
              data-testid="admin-refresh"
              className="text-xs uppercase tracking-[0.22em] font-semibold text-[#5C625E] hover:text-[#C05C3D] flex items-center gap-2"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-xs uppercase tracking-[0.22em] font-semibold text-[#5C625E] hover:text-[#C05C3D] flex items-center gap-2"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12 space-y-16">
        {/* Pricing */}
        <section data-testid="admin-pricing-section">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <span className="text-xs uppercase tracking-[0.28em] text-[#8A9A86] font-semibold">
                Pricing
              </span>
              <h2 className="mt-2 font-serif font-light text-3xl md:text-4xl tracking-tight">
                Rates & discount
              </h2>
            </div>
          </div>

          {settingsDraft ? (
            <form
              onSubmit={saveSettings}
              data-testid="admin-settings-form"
              className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-end bg-[#EAE4D9]/60 p-4 border border-[#2C302E]/10 mb-8"
            >
              <div className="sm:col-span-3">
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                  Base nightly price
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  required
                  value={settingsDraft.base_nightly_price}
                  onChange={(e) =>
                    setSettingsDraft((p) => ({ ...p, base_nightly_price: e.target.value }))
                  }
                  data-testid="settings-base-price"
                  className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                  Currency
                </label>
                <input
                  type="text"
                  required
                  maxLength={8}
                  value={settingsDraft.currency}
                  onChange={(e) =>
                    setSettingsDraft((p) => ({ ...p, currency: e.target.value.toUpperCase() }))
                  }
                  data-testid="settings-currency"
                  className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D] uppercase"
                />
              </div>
              <div className="sm:col-span-3">
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                  Weekly discount %
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  required
                  value={settingsDraft.weekly_discount_percent}
                  onChange={(e) =>
                    setSettingsDraft((p) => ({
                      ...p,
                      weekly_discount_percent: e.target.value,
                    }))
                  }
                  data-testid="settings-discount-percent"
                  className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                  Min nights for discount
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  step="1"
                  required
                  value={settingsDraft.weekly_discount_min_nights}
                  onChange={(e) =>
                    setSettingsDraft((p) => ({
                      ...p,
                      weekly_discount_min_nights: e.target.value,
                    }))
                  }
                  data-testid="settings-discount-min-nights"
                  className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={!!busy._settings}
                  data-testid="settings-save-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#2C302E] hover:bg-[#0e0f0f] text-[#F8F5F0] px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors disabled:opacity-50"
                >
                  {busy._settings ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-[#5C625E] text-sm mb-8">
              <Loader2 className="inline h-3 w-3 animate-spin mr-2" /> Loading pricing…
            </div>
          )}

          {/* Seasonal prices */}
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#5C625E] font-semibold">
                Seasonal prices
              </div>
              <p className="text-sm text-[#5C625E] mt-1 max-w-2xl font-light">
                Override the base rate for a specific date range (e.g. peak summer). Any night not covered by a season uses the base rate.
              </p>
            </div>
          </div>

          <form
            onSubmit={addSeasonal}
            data-testid="admin-add-seasonal-form"
            className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end mb-6 bg-white/60 p-4 border border-[#2C302E]/10"
          >
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                From
              </label>
              <input
                type="date"
                required
                value={newSeasonal.start_date}
                onChange={(e) =>
                  setNewSeasonal((p) => ({ ...p, start_date: e.target.value }))
                }
                data-testid="seasonal-start"
                className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
              />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                To (exclusive)
              </label>
              <input
                type="date"
                required
                value={newSeasonal.end_date}
                onChange={(e) =>
                  setNewSeasonal((p) => ({ ...p, end_date: e.target.value }))
                }
                data-testid="seasonal-end"
                className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                Nightly ({settingsDraft?.currency || "EUR"})
              </label>
              <input
                type="number"
                min="0"
                step="1"
                required
                value={newSeasonal.nightly_price}
                onChange={(e) =>
                  setNewSeasonal((p) => ({ ...p, nightly_price: e.target.value }))
                }
                data-testid="seasonal-price"
                className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                Label
              </label>
              <input
                type="text"
                placeholder="e.g. Summer 2026"
                value={newSeasonal.label}
                onChange={(e) => setNewSeasonal((p) => ({ ...p, label: e.target.value }))}
                data-testid="seasonal-label"
                className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={!!busy._newSeasonal}
                data-testid="seasonal-add-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2C302E] hover:bg-[#0e0f0f] text-[#F8F5F0] px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors disabled:opacity-50"
              >
                {busy._newSeasonal ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Add
              </button>
            </div>
          </form>

          {seasonals.length === 0 ? (
            <div className="text-[#5C625E] text-sm border border-dashed border-[#2C302E]/20 p-6">
              No seasonal overrides — all nights use the base rate.
            </div>
          ) : (
            <div className="border border-[#2C302E]/15 divide-y divide-[#2C302E]/10">
              {seasonals.map((sp) => (
                <div
                  key={sp.id}
                  data-testid={`admin-seasonal-${sp.id}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 bg-white/30"
                >
                  <div>
                    <div className="font-serif text-base text-[#2C302E]">
                      {fmtDate(sp.start_date)} → {fmtDate(sp.end_date)}
                      <span className="ml-3 text-[#C05C3D] font-semibold">
                        {settings?.currency || "EUR"} {sp.nightly_price}
                        <span className="text-[10px] uppercase tracking-[0.2em] font-normal ml-1">
                          / night
                        </span>
                      </span>
                    </div>
                    {sp.label && (
                      <div className="text-xs text-[#5C625E] mt-0.5">{sp.label}</div>
                    )}
                  </div>
                  <button
                    disabled={!!busy[sp.id]}
                    onClick={() => deleteSeasonal(sp.id)}
                    data-testid={`admin-seasonal-delete-${sp.id}`}
                    className="inline-flex items-center gap-1.5 text-[#C05C3D] hover:text-[#a64c30] text-[10px] uppercase tracking-[0.22em] font-semibold transition-colors disabled:opacity-40"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bookings */}
        <section data-testid="admin-bookings-section">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <span className="text-xs uppercase tracking-[0.28em] text-[#8A9A86] font-semibold">
                Requests
              </span>
              <h2 className="mt-2 font-serif font-light text-3xl md:text-4xl tracking-tight">
                Booking requests
              </h2>
            </div>
            <span className="text-sm text-[#5C625E]" data-testid="admin-bookings-count">
              {bookings.length} total
            </span>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-[#5C625E]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-[#5C625E] text-sm border border-dashed border-[#2C302E]/20 p-8">
              No booking requests yet.
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((b) => {
                const nights = nightsBetween(b.check_in, b.check_out);
                const isBusy = !!busy[b.id];
                return (
                  <article
                    key={b.id}
                    data-testid={`admin-booking-${b.id}`}
                    className="border border-[#2C302E]/15 bg-white/50 p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start"
                  >
                    <div className="md:col-span-4">
                      <div className="font-serif text-xl text-[#2C302E]">{b.name}</div>
                      <a
                        href={`mailto:${b.email}`}
                        className="text-sm text-[#1D4B69] hover:underline break-all"
                      >
                        {b.email}
                      </a>
                      {b.phone && (
                        <div className="text-sm text-[#5C625E] mt-1">{b.phone}</div>
                      )}
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] mt-2">
                        Received {fmtDate(b.created_at.slice(0, 10))} · lang {b.language?.toUpperCase()}
                      </div>
                    </div>

                    <div className="md:col-span-4 text-sm">
                      <div className="text-xs uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                        Stay
                      </div>
                      <div className="mt-1 font-serif text-lg text-[#2C302E]">
                        {fmtDate(b.check_in)} → {fmtDate(b.check_out)}
                      </div>
                      <div className="text-[#5C625E] text-xs uppercase tracking-[0.18em] mt-1">
                        {nights} nights · {b.guests} guests
                      </div>
                      {b.total_price ? (
                        <div className="mt-2 text-sm font-serif text-[#2C302E]">
                          {b.currency || "EUR"} {Math.round(b.total_price)}
                          {b.discount_amount ? (
                            <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#8A9A86]">
                              −{Math.round(b.discount_amount)} {b.currency || "EUR"} · {b.discount_percent}%
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      {b.message && (
                        <p className="mt-3 text-sm text-[#2C302E] leading-relaxed font-light italic border-l-2 border-[#EAE4D9] pl-3">
                          "{b.message}"
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-4 flex flex-col items-start md:items-end gap-3">
                      <span
                        className={`inline-block text-[10px] uppercase tracking-[0.22em] font-semibold border px-3 py-1 ${
                          STATUS_STYLES[b.status] || STATUS_STYLES.pending
                        }`}
                        data-testid={`admin-booking-status-${b.id}`}
                      >
                        {b.status}
                      </span>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          disabled={isBusy || b.status === "confirmed"}
                          onClick={() => updateStatus(b.id, "confirmed")}
                          data-testid={`admin-booking-approve-${b.id}`}
                          className="inline-flex items-center gap-1.5 bg-[#8A9A86] hover:bg-[#6f8069] disabled:opacity-40 text-white px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors"
                        >
                          <Check className="h-3 w-3" />
                          Approve
                        </button>
                        <button
                          disabled={isBusy || b.status === "declined"}
                          onClick={() => updateStatus(b.id, "declined")}
                          data-testid={`admin-booking-decline-${b.id}`}
                          className="inline-flex items-center gap-1.5 bg-[#C05C3D] hover:bg-[#a64c30] disabled:opacity-40 text-white px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors"
                        >
                          <X className="h-3 w-3" />
                          Decline
                        </button>
                        {b.status !== "pending" && (
                          <button
                            disabled={isBusy}
                            onClick={() => updateStatus(b.id, "pending")}
                            data-testid={`admin-booking-reset-${b.id}`}
                            className="inline-flex items-center gap-1.5 border border-[#2C302E]/20 hover:border-[#2C302E]/50 text-[#5C625E] px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* Booked Ranges */}
        <section data-testid="admin-ranges-section">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <span className="text-xs uppercase tracking-[0.28em] text-[#8A9A86] font-semibold">
                Calendar
              </span>
              <h2 className="mt-2 font-serif font-light text-3xl md:text-4xl tracking-tight">
                Booked date ranges
              </h2>
            </div>
            <span className="text-sm text-[#5C625E]" data-testid="admin-ranges-count">
              {ranges.length} total
            </span>
          </div>

          <p className="text-sm text-[#5C625E] mb-6 max-w-2xl font-light">
            Add or remove blocked periods that aren't tied to a booking request — e.g. when you confirm a guest by WhatsApp or you want to block dates for personal use.
          </p>

          <form
            onSubmit={addRange}
            data-testid="admin-add-range-form"
            className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-end mb-8 bg-[#EAE4D9]/60 p-4 border border-[#2C302E]/10"
          >
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                Start date
              </label>
              <input
                type="date"
                required
                value={newRange.start_date}
                onChange={(e) =>
                  setNewRange((p) => ({ ...p, start_date: e.target.value }))
                }
                data-testid="admin-range-start"
                className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
              />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                End date (checkout)
              </label>
              <input
                type="date"
                required
                value={newRange.end_date}
                onChange={(e) =>
                  setNewRange((p) => ({ ...p, end_date: e.target.value }))
                }
                data-testid="admin-range-end"
                className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
              />
            </div>
            <div className="sm:col-span-4">
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#5C625E] font-semibold">
                Label (optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Family stay, WhatsApp booking…"
                value={newRange.label}
                onChange={(e) => setNewRange((p) => ({ ...p, label: e.target.value }))}
                data-testid="admin-range-label"
                className="mt-1 w-full bg-white border border-[#2C302E]/20 px-3 py-2 text-sm text-[#2C302E] focus:outline-none focus:border-[#C05C3D]"
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={!!busy._new}
                data-testid="admin-range-add-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2C302E] hover:bg-[#0e0f0f] text-[#F8F5F0] px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors disabled:opacity-50"
              >
                {busy._new ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Add
              </button>
            </div>
          </form>

          {loading ? (
            <div className="flex items-center gap-2 text-[#5C625E]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : ranges.length === 0 ? (
            <div className="text-[#5C625E] text-sm border border-dashed border-[#2C302E]/20 p-8">
              No blocked ranges.
            </div>
          ) : (
            <div className="border border-[#2C302E]/15 divide-y divide-[#2C302E]/10">
              {ranges.map((r) => {
                const linkedToBooking = (r.source || "").startsWith("booking:");
                return (
                  <div
                    key={r.id}
                    data-testid={`admin-range-${r.id}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 bg-white/30"
                  >
                    <div>
                      <div className="font-serif text-base text-[#2C302E]">
                        {fmtDate(r.start_date)} → {fmtDate(r.end_date)}
                        <span className="ml-2 text-[#8A9A86] text-xs">
                          ({nightsBetween(r.start_date, r.end_date)} nights)
                        </span>
                      </div>
                      <div className="text-xs text-[#5C625E] mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{r.label || "Booked"}</span>
                        <span className="text-[10px] uppercase tracking-[0.2em] border border-[#2C302E]/15 px-1.5 py-0.5">
                          {r.source === "seed"
                            ? "seed"
                            : linkedToBooking
                            ? "from request"
                            : "manual"}
                        </span>
                      </div>
                    </div>
                    <button
                      disabled={!!busy[r.id]}
                      onClick={() => deleteRange(r.id)}
                      data-testid={`admin-range-delete-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-[#C05C3D] hover:text-[#a64c30] text-[10px] uppercase tracking-[0.22em] font-semibold transition-colors disabled:opacity-40"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};
